import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, Spinner, Button, Text, Accordion, AccordionItem, AccordionHeader, AccordionPanel } from '@fluentui/react-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, markProcessAsCancelled } from '../../utils/store';
import { HubManager, StopChatRPCProps } from '../../Service/SignalR/HubManager';
import { ChatSegmentRequest } from '../../Service/ServerAPI';
import { useTokenService } from '../../tokenService';
import ReactMarkdown from 'react-markdown';

const useStyles = makeStyles({
    accordionContainer: {
        margin: '8px 0',
        width: '100%',
        boxSizing: 'border-box',
    },
    accordion: {
        backgroundColor: 'transparent',
        border: 'none',
    },
    accordionItem: {
        backgroundColor: 'transparent',
        border: 'none',
    },
    accordionHeader: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#fff',
        padding: '12px 16px',
        transition: 'background-color 0.2s ease, backdrop-filter 0.2s ease, border 0.2s ease',
        cursor: 'pointer',
        borderRadius: '20px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
        },
    },
    accordionHeaderClickable: {
        backgroundColor: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        color: '#fff',
        padding: '12px 16px',
        transition: 'background-color 0.2s ease, backdrop-filter 0.2s ease, border 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer',
        borderRadius: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
        },
    },
    accordionHeaderStatic: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#fff',
        padding: '12px 16px',
        borderRadius: '20px',
        cursor: 'default',
        pointerEvents: 'none',
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
    },
    spinnerContainer: {
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    spinner: {
        color: '#fff',
        '& .fui-Spinner__spinner': {
            color: '#fff',
        },
    },
    stopButton: {
        position: 'absolute',
        backgroundColor: '#fff',
        border: 'none',
        borderRadius: '50%',
        width: '28px',
        height: '28px',
        minWidth: '28px',
        minHeight: '28px',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            cursor: 'not-allowed',
            opacity: '0.6',
        },
    },
    cancelButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        color: '#000',
        fontSize: '11px',
        fontWeight: '500',
        padding: '4px 8px',
        marginRight: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover:not(:disabled)': {
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            cursor: 'not-allowed',
            opacity: '0.6',
        },
    },
    stopIcon: {
        color: '#000',
        fontSize: '14px',
        width: '14px',
        height: '14px',
    },
    stopIconDisabled: {
        color: 'rgba(0, 0, 0, 0.4)',
        fontSize: '14px',
        width: '14px',
        height: '14px',
    },
    headerText: {
        color: '#fff',
        fontSize: '14px',
        fontWeight: '400',
        display: 'inline-flex',
        flex: 1,
    },
    headerTextClickable: {
        color: '#fff',
        fontSize: '14px',
        fontWeight: '500',
        display: 'inline-flex',
        flex: 1,
        textShadow: '0 0 1px rgba(255, 255, 255, 0.3)',
    },
    accordionPanel: {
        padding: '16px 20px',
        backgroundColor: 'rgba(62, 67, 75, 0.3)',
        backdropFilter: 'blur(10px)',
        borderRadius: '0 0 20px 20px',
        margin: '0 20px 15px 20px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderTop: 'none',// '1px solid rgba(77, 79, 81, 0.5)',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        maxHeight: '30vh',
        overflowY: 'auto',
        '::-webkit-scrollbar': {
            width: '4px',
        },
        '::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: '4px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&:hover': {
            scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
        },
    },
    contentText: {
        color: '#fff',
        fontSize: '13px',
        lineHeight: '1.4',
        margin: '0',
    },
    completedMessage: {
        color: 'rgba(144, 238, 144, 0.95)', // Light green for completed messages
        fontSize: '13px',
        lineHeight: '1.4',
        margin: '0',
    },
    processMessage: {
        color: 'rgba(173, 216, 230, 0.95)', // Light blue for process notifications
        fontSize: '13px',
        lineHeight: '1.4',
        margin: '0',
        fontWeight: '500',
    },
    iconContainer: {
        width: '20px',
        height: '20px',
        minWidth: '20px',
        minHeight: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkmark: {
        color: 'rgba(144, 238, 144, 0.95)',
        fontSize: '16px',
    },
    cancelIcon: {
        color: 'rgba(255, 99, 71, 0.95)',
        fontSize: '16px',
        fontWeight: 'bold',
    },
    emptyBox: {
        width: '16px',
        height: '16px',
        border: '2px solid rgba(173, 216, 230, 0.5)',
        borderRadius: '3px',
    },
    messageRow: {
        padding: '8px 12px',
        borderRadius: '6px',
        marginBottom: '6px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
    },
    processMessageRow: {
        padding: '8px 12px',
        borderRadius: '6px',
        marginBottom: '6px',
        backgroundColor: 'rgba(173, 216, 230, 0.12)',
        border: '1px solid rgba(173, 216, 230, 0.3)',
        transition: 'all 0.2s ease',
        animation: 'pulse 2s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': {
            backgroundColor: 'rgba(173, 216, 230, 0.12)',
        },
        '50%': {
            backgroundColor: 'rgba(173, 216, 230, 0.18)',
        },
    },
    completedMessageRow: {
        padding: '8px 12px',
        borderRadius: '6px',
        marginBottom: '6px',
        backgroundColor: 'rgba(144, 238, 144, 0.08)',
        border: '1px solid rgba(144, 238, 144, 0.2)',
        transition: 'all 0.2s ease',
    },
    cancelledMessageRow: {
        padding: '8px 12px',
        borderRadius: '6px',
        marginBottom: '6px',
        backgroundColor: 'rgba(255, 99, 71, 0.08)',
        border: '1px solid rgba(255, 99, 71, 0.2)',
        transition: 'all 0.2s ease',
    },
    cancelledMessage: {
        color: 'rgba(255, 99, 71, 0.95)',
        fontSize: '13px',
        lineHeight: '1.4',
        margin: '0',
    },
    markdownContent: {
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: '#fff',
            fontWeight: '600',
            margin: '12px 0 6px 0',
            lineHeight: '1.4',
        },
        '& h1': {
            fontSize: '19px',
        },
        '& h2': {
            fontSize: '18px',
        },
        '& h3': {
            fontSize: '17px',
        },
        '& h4': {
            fontSize: '16px',
        },
        '& h5': {
            fontSize: '15px',
        },
        '& h6': {
            fontSize: '14px',
        },
        '& p': {
            color: '#fff',
            fontSize: '13px',
            lineHeight: '1.5',
            margin: '4px 0',
        },
        '& ul, & ol': {
            color: '#fff',
            fontSize: '13px',
            lineHeight: '1.5',
            margin: '3px 0 3px 20px',
            paddingLeft: '4px',
        },
        '& li': {
            margin: '3px 0',
        },
        '& strong': {
            fontWeight: '600',
            color: '#fff',
        },
        '& em': {
            fontStyle: 'italic',
            color: '#fff',
        },
        '& code': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '12px',
        },
        '& pre': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '8px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
        },
        '& blockquote': {
            borderLeft: '4px solid rgba(255, 255, 255, 0.3)',
            paddingLeft: '12px',
            margin: '8px 0',
            color: '#fff',
            fontStyle: 'italic',
        },
    },
    tinySpinner: {
        color: '#fff',
        marginRight: '8px',
        '& .fui-Spinner__spinner': {
            color: '#fff',
        },
    },
    processingMessageContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
    },
});

interface ReasoningAccordionProps {
    messages: string[];
    processing: boolean;
    defaultOpen?: boolean;
    processNotificationIndex?: number;
}

const ReasoningAccordion: React.FC<ReasoningAccordionProps> = ({
    messages,
    processing,
    defaultOpen = true,
    processNotificationIndex
}) => {
    const classes = useStyles();
    const [isAborting, setIsAborting] = useState(false);
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
    const sessionId = useSelector((state: RootState) => state.settings.sessionId);
    const settings = useSelector((state: RootState) => state.settings);
    const dispatch = useDispatch();
    const tokenService = useTokenService();
    const hubManager = HubManager.getInstance(tokenService);
    const accordionRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // Update isOpen when defaultOpen prop changes (processing -> completed transition)
    useEffect(() => {
        setIsOpen(defaultOpen);
    }, [defaultOpen]);

    // Auto-collapse when processing completes
    const prevProcessing = useRef(processing);
    useEffect(() => {
        if (prevProcessing.current && !processing) {
            setIsOpen(false);
        }
        prevProcessing.current = processing;
    }, [processing]);

    useEffect(() => {
        // No custom scrolling needed - ChatUI will handle scrolling to chatEndRef
        // when accordion content changes
    }, [isOpen]);

    useEffect(() => {
        if (panelRef.current) {
            panelRef.current.scrollTop = panelRef.current.scrollHeight;
        }
    }, [messages]);

    const handleStopExecution = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent accordion toggle
        setIsAborting(true);
        
        // Mark the current process notification as cancelled immediately
        dispatch(markProcessAsCancelled());
        
        if (sessionId) {
            try {
                // Find the last ChatSegmentRequest (User message) to get current user input
                const lastUserMessage = [...settings.chat].reverse().find(
                    (message): message is ChatSegmentRequest =>
                        message.role === 'User' && 'userInput' in message
                );
                
                const stopChatProps: StopChatRPCProps = {
                    sessionId: sessionId,
                    currentUserInput: lastUserMessage?.userInput || '',
                    recentNotificationMessages: settings.recentReasoningNotifications
                };
                await hubManager.stopChatExecution(stopChatProps);
            } catch (error) {
                console.error('Failed to stop chat execution:', error);
                setIsAborting(false); // Reset on error
            }
        } else {
            console.warn('No session ID available to stop execution');
            setIsAborting(false); // Reset if no session ID
        }
    };


    const renderPulsingText = (text: string) => {
        return text.split('').map((char, index) => (
            <motion.span
                key={index}
                animate={{
                    opacity: [0.6, 0.9, 0.6],
                    filter: [
                        'drop-shadow(0 0 1px rgba(255, 255, 255, 0.15))',
                        'drop-shadow(0 0 4px rgba(255, 255, 255, 0.35)) drop-shadow(0 0 6px rgba(255, 255, 255, 0.25))',
                        'drop-shadow(0 0 1px rgba(255, 255, 255, 0.15))'
                    ]
                }}
                transition={{
                    duration: 3,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    repeat: Infinity,
                    delay: index * 0.15,
                }}
                style={{
                    display: 'inline-block',
                }}
            >
                {char === ' ' ? '\u00A0' : char}
            </motion.span>
        ));
    };

    const renderStaticText = (text: string) => {
        return (
            <div className={classes.markdownContent}>
                <ReactMarkdown>{text}</ReactMarkdown>
            </div>
        );
    };

    const renderMessageText = (text: string, index: number) => {
        // Check if this message is a processing notification
        const isProcessingNotification = processNotificationIndex !== undefined && index === processNotificationIndex;
        
        // Check if this is a completed process message (starts with checkmark)
        const isCompletedProcess = text.startsWith('✓ ');
        
        // Check if this is a cancelled process message (starts with X)
        const isCancelledProcess = text.startsWith('✗ ');
        
        let displayText = text;
        if (isCompletedProcess || isCancelledProcess) {
            displayText = text.substring(2);
        }
        
        if (isProcessingNotification) {
            // Render as pulsing text with empty box indicator
            return (
                <div className={classes.processMessageRow}>
                    <div className={classes.processingMessageContainer}>
                        <div className={classes.iconContainer}>
                            <div className={classes.emptyBox} />
                        </div>
                        <div className={`${classes.markdownContent} ${classes.processMessage}`}>
                            {renderPulsingText(displayText)}
                        </div>
                    </div>
                </div>
            );
        } else if (isCancelledProcess) {
            // Render cancelled process with red X
            return (
                <div className={classes.cancelledMessageRow}>
                    <div className={classes.processingMessageContainer}>
                        <div className={classes.iconContainer}>
                            <span className={classes.cancelIcon}>✗</span>
                        </div>
                        <div className={`${classes.markdownContent} ${classes.cancelledMessage}`}>
                            <ReactMarkdown>{displayText}</ReactMarkdown>
                        </div>
                    </div>
                </div>
            );
        } else if (isCompletedProcess) {
            // Render completed process with checkmark
            return (
                <div className={classes.completedMessageRow}>
                    <div className={classes.processingMessageContainer}>
                        <div className={classes.iconContainer}>
                            <span className={classes.checkmark}>✓</span>
                        </div>
                        <div className={`${classes.markdownContent} ${classes.completedMessage}`}>
                            <ReactMarkdown>{displayText}</ReactMarkdown>
                        </div>
                    </div>
                </div>
            );
        } else {
            // Render as static markdown with subtle background
            return (
                <div className={classes.messageRow}>
                    {renderStaticText(text)}
                </div>
            );
        }
    };

    return (
        <motion.div
            ref={accordionRef}
            className={classes.accordionContainer}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
                duration: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94]
            }}
        >
            <div className={classes.accordion}>
                <div className={classes.accordionItem}>
                    <div
                        className={classes.accordionHeaderClickable}
                        onClick={() => setIsOpen(!isOpen)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={classes.headerContent}>
                            <div className={processing ? classes.headerText : classes.headerTextClickable}>
                                {processing
                                    ? renderPulsingText(isAborting ? "Aborting..." : "Processing...")
                                    : "Reasoning"
                                }
                            </div>
                            {processing && hubManager.connectionState === "Connected" && (
                                <button
                                    className={classes.cancelButton}
                                    onClick={handleStopExecution}
                                    disabled={isAborting}
                                    style={{
                                        pointerEvents: isAborting ? 'none' : 'auto',
                                        opacity: isAborting ? 0.6 : 1
                                    }}
                                >
                                    {isAborting ? "Canceling..." : "Cancel"}
                                </button>
                            )}
                            <motion.div
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <ChevronDown20Regular style={{ color: '#fff' }} />
                            </motion.div>
                        </div>
                    </div>
                    <AnimatePresence>
                        {isOpen && (
                            <motion.div
                                key="accordion-panel"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{
                                    height: 'auto',
                                    opacity: 1,
                                    y: 0,
                                    transition: {
                                        duration: 0.4,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                        opacity: { duration: 0.25 },
                                        y: { duration: 0.3 }
                                    }
                                }}
                                exit={{
                                    height: 0,
                                    opacity: 0,
                                    y: -10,
                                    transition: {
                                        duration: 0.3,
                                        ease: [0.4, 0, 0.6, 1],
                                        opacity: { duration: 0.2 },
                                        y: { duration: 0.25 },
                                        height: { duration: 0.3, delay: 0.05 }
                                    }
                                }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div ref={panelRef} className={classes.accordionPanel}>
                                    <AnimatePresence mode="popLayout">
                                        {messages.map((message, index) => (
                                            <motion.div
                                                key={`message-${index}`}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                    transition: {
                                                        duration: 0.3,
                                                        delay: index * 0.1,
                                                        ease: [0.25, 0.46, 0.45, 0.94]
                                                    }
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    y: -8,
                                                    scale: 0.95,
                                                    transition: {
                                                        duration: 0.2,
                                                        delay: (messages.length - 1 - index) * 0.05,
                                                        ease: [0.4, 0, 0.6, 1]
                                                    }
                                                }}
                                                className={classes.contentText}
                                                style={{
                                                    marginBottom: index < messages.length - 1 ? '8px' : '0',
                                                    display: 'block'
                                                }}
                                            >
                                                {renderMessageText(message, index)}
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

export default ReasoningAccordion;